import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isSupportedLocale } from '@clari/i18n'
import { SUPPORTED_SIGNUP_COUNTRIES } from '@clari/shared'
import { Button, Card, Input } from '@clari/ui'
import { AuthLayout } from '@/components/AuthLayout'
import { ErrorBanner } from '@/components/ErrorBanner'
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter'
import { apiErrorMessage } from '@/lib/api-error'
import { authApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/authStore'
import { useSessionStore } from '@/stores/sessionStore'

type Step = 1 | 2 | 'done'

export function Signup() {
  const { t, i18n } = useTranslation('auth')
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const resetSession = useSessionStore((s) => s.reset)

  const [step, setStep] = useState<Step>(1)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [country, setCountry] = useState('FR')
  const [accept, setAccept] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [resent, setResent] = useState(false)

  const lang = i18n.resolvedLanguage ?? i18n.language
  const regionNames = new Intl.DisplayNames([lang], { type: 'region' })

  function goToProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setStep(2)
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (!accept) return
    // Permissive name check (mirror of the server): block digits only — Swan
    // rejects names with digits; everything else (apostrophes, hyphens, accents)
    // is allowed. Swan is the final authority.
    if (/\d/.test(firstName) || /\d/.test(lastName)) {
      setError(t('signup.name_invalid'))
      return
    }
    setLoading(true)
    try {
      const res = await authApi.register({
        email,
        password,
        preferred_language: isSupportedLocale(lang) ? lang : 'en',
        country_residence: country,
        ...(firstName ? { first_name: firstName } : {}),
        ...(lastName ? { last_name: lastName } : {}),
        accept_terms: true,
      })
      setSession({ user: res.user, accessToken: res.access_token, refreshToken: res.refresh_token })
      resetSession()
      setStep('done')
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function resend() {
    try {
      await authApi.resendVerification()
      setResent(true)
    } catch {
      /* keep the same neutral confirmation */
      setResent(true)
    }
  }

  return (
    <AuthLayout>
      <Card>
        {step === 'done' ? (
          <>
            <h1 className="text-2xl font-semibold">{t('signup.done_title')}</h1>
            <p className="mt-1 text-text-muted">{t('signup.done_body', { email })}</p>
            <div className="mt-6 flex flex-col gap-3">
              <Button onClick={() => navigate('/app', { replace: true })} withSignatureDot>
                {t('signup.open_app')}
              </Button>
              <button
                type="button"
                onClick={resend}
                className="text-sm text-text-muted hover:text-text"
              >
                {resent ? t('signup.resent') : t('signup.resend_email')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">{t('signup.title')}</h1>
            <p className="mt-1 text-text-muted">{t('signup.subtitle')}</p>
            <p className="mt-2 text-xs text-text-subtle">
              {step === 1 ? t('signup.step_account') : t('signup.step_profile')}
            </p>

            {step === 1 ? (
              <form className="mt-6 flex flex-col gap-4" onSubmit={goToProfile}>
                <Input
                  label={t('signup.email_label')}
                  type="email"
                  autoComplete="email"
                  value={email}
                  placeholder={t('signup.email_placeholder')}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <div>
                  <Input
                    label={t('signup.password_label')}
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    hint={t('signup.password_hint')}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={12}
                  />
                  <PasswordStrengthMeter password={password} />
                </div>
                <Button type="submit">{t('actions.continue')}</Button>
              </form>
            ) : (
              <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit}>
                <Input
                  label={t('signup.first_name_label')}
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <Input
                  label={t('signup.last_name_label')}
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
                <div className="clari-field">
                  <label className="clari-field__label" htmlFor="country">
                    {t('signup.country_label')}
                  </label>
                  <select
                    id="country"
                    className="clari-input"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  >
                    {SUPPORTED_SIGNUP_COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {regionNames.of(c) ?? c}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="flex items-start gap-2 text-sm text-text-muted">
                  <input
                    type="checkbox"
                    checked={accept}
                    onChange={(e) => setAccept(e.target.checked)}
                  />
                  <span>{t('signup.accept_terms')}</span>
                </label>
                <p className="text-xs text-text-subtle">{t('signup.swan_disclaimer')}</p>

                {error ? <ErrorBanner message={error} /> : null}

                <div className="flex gap-2">
                  <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                    {t('actions.back')}
                  </Button>
                  <Button type="submit" disabled={loading || !accept}>
                    {t('signup.submit')}
                  </Button>
                </div>
              </form>
            )}

            <span className="mt-4 inline-block text-sm text-text-muted">
              {t('signup.already_have_account')}{' '}
              <Link to="/login" className="font-medium text-text underline">
                {t('signup.signin_link')}
              </Link>
            </span>
          </>
        )}
      </Card>
    </AuthLayout>
  )
}
